import * as angular from 'angular';
import * as _ from 'lodash';

import * as Fields from '../../../common/helpers/contextual';
import { ComponentDefinition, PropertyType, ContextualField, AggregationMode } from '../../../models/';
import { ChartIdentifierWidgetConfiguration } from '../chartByIdentifier.configuration';
import { ColoringMode } from '../../../models/widgets/ColoringMode';

class ChartByIdentifierContextualController implements angular.IComponentController {
    configuration: ChartIdentifierWidgetConfiguration;
    formController: angular.IFormController;
    isLoading: boolean;
    properties: ContextualField[];
    
    readonly cancel: () => void;
    readonly save: () => void;

    private _originalConfiguration: ChartIdentifierWidgetConfiguration;
    private _garbages: (() => void)[] = [];

    static $inject = ['$scope', '$q'];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService
    ) {
    }

    $onInit() {
        this.isLoading = true;

        // sanitize
        if (!this.configuration.nameConfiguration) {
            this.configuration.nameConfiguration = {
                hasName: true,
                hasGroupName: false,
                hasShortComment: false,
                hasUnit: true,
                hasValueType: false
            };
        }
        this._originalConfiguration = angular.copy(this.configuration);
        this.initProperties();

        this._garbages.push(this._$scope.$watch(() => this.configuration, () => {
            _.find(this.properties, p => p.propertyName === 'aggregationPeriodProperty')
                .disabled = this.configuration.aggregation.mode === AggregationMode.None;
        }, true));

        this.isLoading = false;
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    onSave(): angular.IPromise<void> {
        return this._$q.when(this.save());
    }

    onCancel() {
        this.configuration = angular.copy(this._originalConfiguration);
        this.cancel();
    }

    private initProperties() {
        const colorField: ContextualField = angular.copy(Fields.Color);
        colorField.disabled = this.configuration.options.coloringMode === ColoringMode.BySerie
            && !this.configuration.options.isLineVisible;

        const coloringMode: ContextualField = angular.copy(Fields.ColoringMode);
        coloringMode.onChange = (viewValue: number) => {
            colorField.disabled = viewValue === ColoringMode.BySerie
                && !this.configuration.options.isLineVisible;
        };

        const barsSpacing = {
            modelName: 'barsSpacing',
            propertyType: PropertyType.Number,
            propertyName: 'barsSpacingProperty',
            label: 'Bars spacing',
            placeholder: 'Bars spacing',
            icon: ['fa-arrows-v', 'fa-bars'],
            disabled: !this.configuration.options.isRectangleVisible,
            extraData: {
                subProperty: 'options',
                min: 0,
                max: 80,
                decimals: 0
            },
            messages: [
                { key: 'min', value: 'Bars spacing must be a number between 0 and 80.' },
                { key: 'max', value: 'Bars spacing must be a number between 0 and 80.' },
                { key: 'pattern', value: 'Bars spacing must be a number between 0 and 80.' },
                { key: 'float', value: 'Bars spacing must be a number between 0 and 80.' }
            ]
        };
        this.properties = [
            angular.copy(Fields.Title),
            angular.copy(Fields.IsTitleVisible),
            angular.copy(Fields.IsHorizontalLineVisible),
            coloringMode,
            colorField,
            {
                modelName: 'isRectangleVisible',
                propertyType: PropertyType.Boolean,
                propertyName: 'isRectangleVisibleProperty',
                label: 'Bars',
                placeholder: 'Bars',
                icon: 'fa-bar-chart',
                onChange: (viewValue: boolean) => {
                    barsSpacing.disabled = !viewValue;
                },
                extraData: { subProperty: 'options' }
            },
            barsSpacing,
            {
                modelName: 'isLineVisible',
                propertyType: PropertyType.Boolean,
                propertyName: 'isLineVisibleProperty',
                label: 'Line',
                placeholder: 'Line',
                icon: 'fa-line-chart',
                onChange: (viewValue: boolean) => {
                    colorField.disabled = this.configuration.options.coloringMode === 1
                        && !viewValue;
                },
                extraData: { subProperty: 'options' }
            },
            {
                modelName: 'isCircleVisible',
                propertyType: PropertyType.Boolean,
                propertyName: 'isCircleVisibleProperty',
                label: 'Circle',
                placeholder: 'Circle',
                icon: 'fa-circle',
                extraData: { subProperty: 'options' }
            },
            {
                modelName: 'isLabelsVisible',
                propertyType: PropertyType.Boolean,
                propertyName: 'isLabelsVisibleProperty',
                label: 'Labels',
                placeholder: 'Labels',
                icon: 'fa-hashtag',
                extraData: { subProperty: 'options' }
            },
            angular.copy(Fields.YMin),
            angular.copy(Fields.YMax),
            angular.copy(Fields.AggregationMode),
            Fields.AggregationPeriodBuilder(this.configuration),
            angular.copy(Fields.MinPeriod),
            angular.copy(Fields.MaxPeriod),
            angular.copy(Fields.IsEndDateIncluded),
            angular.copy(Fields.NameConfiguration.HasGroupName),
            angular.copy(Fields.NameConfiguration.HasName),
            angular.copy(Fields.NameConfiguration.HasShortComment),
            angular.copy(Fields.NameConfiguration.HasValueType),
            angular.copy(Fields.NameConfiguration.HasUnit),
            angular.copy(Fields.PresentationUnit),
            {
                modelName: 'orderBy',
                propertyType: PropertyType.Select,
                propertyName: 'orderByProperty',
                label: 'Order by',
                icon: 'fa-sort-amount-desc',
                extraData: {
                    subProperty: 'options',
                    options: [
                        {
                            key: 0,
                            value: 'By Series'
                        }, {
                            key: 1,
                            value: 'Largest to smallest'
                        }, {
                            key: 2,
                            value: 'Smallest to largest'
                        }
                    ]
                }
            }
        ];
    }
}

export const wtChartByIdentifierContextual: ComponentDefinition = {
    componentId: 'wtChartByIdentifierContextual',
    controller: ChartByIdentifierContextualController,
    template: require('./chartByIdentifierContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=',
        configuration: '=',
        save: '&?',
        cancel: '&?'
    }
};